import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import BillingAddress from 'components/BillingAddress/BillingAddress';
import UpdateBillingAddressForm from 'components/UpdateBillingAddressForm/UpdateBillingAddressForm';
import { type FC, type MouseEventHandler, useCallback, useMemo, useRef, useState } from 'react';
import type { Address, NewAddress, OnSubmit, UpdateBillingAddressCallback } from 'types/common';
import styles from './UpdateBillingAddress.module.css';

interface UpdateBillingAddressProps {
  updateBillingAddress: UpdateBillingAddressCallback;
  existingAddress?: Address;
  className?: string;
  readOnly?: boolean;
  hasAddress?: boolean;
}

const UpdateBillingAddress: FC<UpdateBillingAddressProps> = ({
  className,
  updateBillingAddress,
  existingAddress,
  readOnly = false,
  hasAddress = !!existingAddress,
}) => {
  const submitRef = useRef<OnSubmit>();
  const [editing, setEditing] = useState(false);

  const [serverRequest, setServerRequest] = useState<{
    isLoading: boolean;
    serverError: string | null;
  }>({
    isLoading: false,
    serverError: null,
  });

  const onSubmit = useCallback(
    async (newAddress?: NewAddress) => {
      if (!newAddress || serverRequest.isLoading) {
        return;
      }

      setServerRequest({
        isLoading: true,
        serverError: null,
      });

      try {
        await updateBillingAddress(newAddress);
      } catch (err: any) {
        setServerRequest({
          isLoading: false,
          serverError: err,
        });

        return;
      }

      setServerRequest({
        isLoading: false,
        serverError: null,
      });
      setEditing(false);
    },
    [serverRequest.isLoading, updateBillingAddress]
  );

  const handleSubmit: MouseEventHandler &
    MouseEventHandler<HTMLAnchorElement> &
    MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };

  const handleCancel = useCallback(() => {
    if (serverRequest.isLoading) {
      return;
    }
    setEditing(false);
  }, [serverRequest.isLoading]);

  const { panelContent, noContentPadding } = useMemo(() => {
    if (editing) {
      return {
        panelContent: (
          <div className={styles.content}>
            <UpdateBillingAddressForm
              existingAddress={existingAddress}
              onSubmit={onSubmit}
              ref={submitRef}
              onCancel={handleCancel}
            />
            {serverRequest.serverError && (
              <BannerNotification variant="error">{serverRequest.serverError}</BannerNotification>
            )}
          </div>
        ),
        noContentPadding: false,
      };
    }

    if (existingAddress) {
      return {
        panelContent: (
          <div className={styles.addressContent}>
            <BillingAddress address={existingAddress} />
            {!readOnly && (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Update billing address
                </Button>
              </div>
            )}
          </div>
        ),
        noContentPadding: false,
      };
    }
    if (hasAddress) {
      return {
        panelContent: 'A billing address has been saved to your account.',
        noContentPadding: false,
      };
    }

    if (readOnly) {
      return {
        panelContent: (
          <PanelNotification label="No saved billing address" className={styles.emptyUnactionable}>
            A billing address is required to refill your account, pay your balance, activate SIMs,
            and change data plans. Contact an admin in your organization to make changes or for more
            details.
          </PanelNotification>
        ),
        noContentPadding: true,
      };
    }

    return {
      panelContent: (
        <PanelNotification
          className={styles.empty}
          actions={
            <Button
              iconStart="Mailbox"
              onClick={() => {
                setEditing(true);
              }}
            >
              Add a billing address
            </Button>
          }
        >
          We require a billing address for tax and regulatory purposes
        </PanelNotification>
      ),
      noContentPadding: true,
    };
  }, [
    editing,
    existingAddress,
    handleCancel,
    hasAddress,
    onSubmit,
    readOnly,
    serverRequest.serverError,
  ]);

  return (
    <Panel
      className={className}
      noContentPadding={noContentPadding}
      header="Billing address"
      contentWell={editing && 'We require a billing address for tax and regulatory purposes.'}
      footer={
        hasAddress &&
        !existingAddress &&
        readOnly &&
        'Contact an admin in your organization to make changes or for more details.'
      }
      footerVariant={editing ? 'default' : 'alternative'}
      footerActions={
        editing ? (
          <>
            <Button variant="secondary" disabled={serverRequest.isLoading} onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={serverRequest.isLoading} onClick={handleSubmit}>
              Save address
            </Button>
          </>
        ) : undefined
      }
    >
      {panelContent}
    </Panel>
  );
};

export default UpdateBillingAddress;
